import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';

interface MFAChallengeDialogProps {
  open: boolean;
  onVerify: (code: string, session: string, username?: string) => Promise<void>;
  session: string;
  username?: string;
  loading?: boolean;
}

const MFAChallengeDialog = ({ open, onVerify, session, username, loading = false }: MFAChallengeDialogProps) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError('Code must be 6 digits');
      return;
    }

    if (!/^\d{6}$/.test(code)) {
      setError('Code must contain only numbers');
      return;
    }

    try {
      await onVerify(code, session, username);
      // Reset form on success
      setCode('');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Prevent closing by clicking outside or pressing escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h5" component="div">
            Two-Factor Authentication
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enter the 6-digit code from your authenticator app
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code shown for this account.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            label="Authentication Code"
            value={code}
            onChange={handleCodeChange}
            margin="normal"
            required
            disabled={loading}
            autoFocus
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
              inputMode: 'numeric',
            }}
            helperText="Enter the 6-digit code from your authenticator app"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || code.length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MFAChallengeDialog;

